import { Injectable, signal } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { UserProfile, PerfilId } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _currentUser = signal<UserProfile | null>(null);
  readonly currentUser = this._currentUser.asReadonly();

  constructor(private supabaseService: SupabaseService) {}

  async loadCurrentUser(): Promise<UserProfile | null> {
    // Return cached value if already loaded
    if (this._currentUser()) return this._currentUser();

    const session = await this.supabaseService.getSession();
    if (!session) {
      this._currentUser.set(null);
      return null;
    }

    const { data } = await this.supabaseService.client
      .from('tb_usuario')
      .select('id_usuario, nome, email, id_perfil, id_empresa, status')
      .eq('id_usuario', session.user.id)
      .single();

    if (data) {
      this._currentUser.set(data as UserProfile);
      return data as UserProfile;
    }

    // Fallback: build from session metadata
    const meta = session.user.user_metadata;
    const profile: UserProfile = {
      id_usuario: session.user.id,
      nome: meta?.['nome'] ?? session.user.email ?? '',
      email: session.user.email ?? '',
      id_perfil: meta?.['id_perfil'] ?? PerfilId.Admin,
      id_empresa: meta?.['id_empresa'] ?? null,
    };
    this._currentUser.set(profile);
    return profile;
  }

  clearCurrentUser(): void {
    this._currentUser.set(null);
  }

  /** Atualiza campos do usuário em cache (ex.: após editar o perfil). */
  patchCurrentUser(patch: Partial<UserProfile>): void {
    const atual = this._currentUser();
    if (!atual) return;
    this._currentUser.set({ ...atual, ...patch });
  }

  getRedirectRoute(idPerfil: PerfilId): string {
    switch (idPerfil) {
      case PerfilId.Admin:
        return '/admin';
      case PerfilId.CompanyAdmin:
        return '/empresa';
      case PerfilId.PartyOwner:
        return '/cliente';
      default:
        return '/home';
    }
  }

  async signOut(): Promise<void> {
    await this.supabaseService.signOut();
    this._currentUser.set(null);
  }
}
